import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@material-ui/core'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import ContentHelp from '@flatsy/core/Content/Help'
import FlatsyOTPInput from '@flatsy/core/FlatsyOTPInput'
import { Body1 } from '@flatsy/core/MaterialUi/Typo'
import { sentenceCase } from '@flatsy/utils/Functions/format/StringCases'
import { useAppDispatch } from 'src/redux'
import { AlertSlice } from 'src/redux/slices/Alert'

import { useStyles } from './Style'

interface FormData {
  code: string
}

const CODE_LENGTH = 5

interface CheckCodeProps {
  submit: (code: string) => Promise<boolean>
  requestNewCode: () => void
}

const CheckCode: React.FC<CheckCodeProps> = ({ submit, requestNewCode }) => {
  const classes = useStyles()
  const i18n = useTranslation()
  const dispatch = useAppDispatch()

  const schema = yup.object({
    code: yup
      .string()
      .required()
      .matches(/^[A-Z0-9]{5}$/, i18n.t('the_code_is_invalid')),
  })

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    setError,
    clearErrors,
  } = useForm<FormData>({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues: { code: '' },
  })

  const onSubmit = async (data: FormData) => {
    const success = await submit(data.code)
    if (!success) {
      setError('code', {
        type: 'manual',
        message: i18n.t('invalid_code'),
      })
      dispatch(
        AlertSlice.actions.setAlert({
          severity: 'error',
          content: i18n.t('invalid_code'),
          open: true,
          icon: <ErrorOutlineIcon />,
        })
      )
    }
  }

  const handleCodeChange = (onChange: (value: string) => void) => (code: string) => {
    onChange(code)
    if (errors.code) {
      clearErrors('code')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      <ContentHelp icon={<InfoOutlinedIcon />} severity="info">
        {i18n.t('the_code_is_valid_for_10_minutes')}
      </ContentHelp>
      <div className={classes.inputContent}>
        <Body1>{i18n.t('enter_the_code_received_by_email')}</Body1>
        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <FlatsyOTPInput
              length={CODE_LENGTH}
              onChange={handleCodeChange(field.onChange)}
              autoFocus
              hasError={!!errors.code}
            />
          )}
        />
      </div>
      <div className={classes.buttonContainer}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.continueButton}
          disabled={!isValid}
        >
          {sentenceCase(i18n.t('confirm'))}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => requestNewCode()}
          className={classes.newCodeButton}
        >
          {i18n.t('send_a_new_code')}
        </Button>
      </div>
    </form>
  )
}

export default CheckCode
